/**
 * 크롤링된 상품 데이터의 기본 구조를 생성하는 팩토리 함수
 * DB에 저장되는 원본 크롤링 데이터
 */
export const createCrawledProductData = () => ({
  // 기본 상품 정보
  id: null, // 유니크 id
  product_id: '', // 제품 ID
  title: '', // 상품명 (원본)
  title_en: null, // 영문 제목 (쇼피파이용)
  description: null, // 상품 설명
  price: null, // 가격
  price_usd: null, // 미국 달러 가격
  compare_price: null, // 비교 가격 (할인 전 가격)
  sale_status: '', // 판매 상태
  pre_order_due_date: null, // 예약 주문 마감일

  // 카테고리 및 분류
  category: '', // 카테고리
  category_en: null, // 카테고리(영문)
  category_id: null, // 카테고리 ID
  product_type: null, // 제품 타입
  vendor: null, // 브랜드/벤더
  brand_name: null, // 브랜드명 (한국어)
  brand_name_en: null, // 브랜드명 (영어)
  tags: null, // 태그 목록
  
  // 크롤링 메타데이터
  source_site: '', // 출처 사이트 (musinsa, oliveyoung, weverse 등)
  source_url: '', // 원본 상품 URL
  crawled_at: new Date(), // 크롤링 시점
  
  // 이미지 관련
  // rep_image: null, // 대표 이미지 URL
  // images: null, // 상품 이미지 URL 목록
  content_images: null, // 상품 상세 이미지 URL 목록
  media_images: null, // 미디어 갤러리 이미지 URL 목록
  
  // 상품 상세 정보
  body_html: null, // HTML 본문 (원본)
  arrival_date: null, // 도착일 (start, end 포함) { start: string, end: string }
  shipment_date: null, // 출하일 (start, end 포함) { start: string, end: string }
  
  // 특수 필드 (사이트별)
  group_name: null, // 그룹명
  artist: null, // 아티스트명
  
  // 재고 관리
  stock_quantity: 0, // 재고 수량
  allow_stock_tracking: null, // 재고 추적 허용
  track_inventory: null, // 재고 추적
  
  // 상태 관리
  is_active: true, // 활성 상태
  status: 'draft', // 상태 (draft, active, archived)
  
  // 쇼피파이 연동 정보
  shopify_product_id: null, // 쇼피파이 제품 ID
  shopify_status: null, // 쇼피파이 상태
  published_at: null, // 쇼피파이 게시 시점
  published_to_shopify: false, // 쇼피파이 게시 여부
});

/**
 * 크롤링된 상품 데이터의 기본값 (하위 호환성을 위해 유지)
 * @deprecated createCrawledProductData() 사용을 권장합니다.
 */
export const CrawledProductData = createCrawledProductData();

/**
 * 상품 목록 조회 시 사용되는 페이지네이션 정보를 생성하는 팩토리 함수
 */
export const createProductPagination = () => ({
  total: 0,
  page: 1,
  size: 10,
  pages: 0,
  has_next: false,
  has_prev: false
});

/**
 * 상품 목록 조회 시 사용되는 페이지네이션 정보 (하위 호환성을 위해 유지)
 * @deprecated createProductPagination() 사용을 권장합니다.
 */
export const ProductPagination = createProductPagination();

/**
 * 상품 목록 조회 응답 데이터를 생성하는 팩토리 함수
 */
export const createProductListResponse = () => ({
  items: [],
  total: 0,
  page: 1,
  size: 10,
  pages: 0,
  has_next: false,
  has_prev: false
});

/**
 * 상품 목록 조회 응답 데이터 (하위 호환성을 위해 유지)
 * @deprecated createProductListResponse() 사용을 권장합니다.
 */
export const ProductListResponse = createProductListResponse();

/**
 * 카테고리 검색 결과 데이터를 생성하는 팩토리 함수
 */
export const createCategorySearchResult = () => ({
  id: null,
  name: '',
  name_en: null,
  site: '',
  parent_id: null,
  level: 0,
  path: '',
  created_at: null,
  updated_at: null
});

/**
 * 카테고리 검색 결과 데이터 (하위 호환성을 위해 유지)
 * @deprecated createCategorySearchResult() 사용을 권장합니다.
 */
export const CategorySearchResult = createCategorySearchResult();

/**
 * 크롤링 요청 데이터를 생성하는 팩토리 함수
 */
export const createCrawlRequest = (site = '') => {
  const baseRequest = {
    source_site: site, // 사이트 코드 (musinsa, oliveyoung, weverse 등)
    url: '' // 크롤링할 상품 URL
  };

  // 위버스 사이트인 경우 artist 필드를 딕셔너리 형태로 추가 (크롤링 시에만)
  if (site === 'weverse') {
    baseRequest.artist = {
      name: '', // 아티스트명
      id: null // 아티스트 ID (선택사항)
    };
  }

  return baseRequest;
};

/**
 * 크롤링 요청 데이터 (하위 호환성을 위해 유지)
 * @deprecated createCrawlRequest() 사용을 권장합니다.
 */
export const CrawlRequest = createCrawlRequest();

/**
 * 크롤링 응답 데이터를 생성하는 팩토리 함수
 */
export const createCrawlResponse = () => ({
  success: false,
  data: null,
  message: '',
  error: null
});

/**
 * 크롤링 응답 데이터 (하위 호환성을 위해 유지)
 * @deprecated createCrawlResponse() 사용을 권장합니다.
 */
export const CrawlResponse = createCrawlResponse();

/**
 * 상품 데이터 유효성 검사 함수
 * @param {Object} product - 검사할 상품 데이터
 * @returns {boolean} 유효한 데이터인지 여부
 */
export const isValidProductData = (product) => {
  if (!product || typeof product !== 'object') {
    return false;
  }
  
  // 필수 필드 검사
  const requiredFields = ['product_id', 'title', 'category', 'source_site', 'source_url'];
  return requiredFields.every(field => product[field] !== undefined && product[field] !== null && product[field] !== '');
};

/**
 * 상품 데이터를 API 응답 형식으로 변환하는 함수
 * @param {Object} product - 변환할 상품 데이터
 * @returns {Object} API 응답 형식의 상품 데이터
 */
export const formatProductForAPI = (product) => {
  const formatted = { ...product };
  
  // 날짜 필드 변환
  if (formatted.crawled_at && typeof formatted.crawled_at === 'string') {
    formatted.crawled_at = new Date(formatted.crawled_at);
  }
  
  if (formatted.published_at && typeof formatted.published_at === 'string') {
    formatted.published_at = new Date(formatted.published_at);
  }
  
  return formatted;
};

/**
 * 상품 데이터를 표시용으로 변환하는 함수
 * @param {Object} product - 변환할 상품 데이터
 * @returns {Object} 표시용 상품 데이터
 */
export const formatProductForDisplay = (product) => {
  const formatted = { ...product };
  
  // 가격 포맷팅
  if (formatted.price) {
    formatted.formatted_price = `₩${formatted.price.toLocaleString()}`;
  }
  
  if (formatted.price_usd) {
    formatted.formatted_price_usd = `$${formatted.price_usd.toFixed(2)}`;
  }
  
  // 날짜 포맷팅
  if (formatted.crawled_at) {
    formatted.formatted_crawled_at = new Date(formatted.crawled_at).toLocaleDateString('ko-KR');
  }
  
  if (formatted.published_at) {
    formatted.formatted_published_at = new Date(formatted.published_at).toLocaleDateString('ko-KR');
  }
  
  return formatted;
};

/**
 * 상품 상태 상수
 */
export const PRODUCT_STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  ARCHIVED: 'archived'
};

/**
 * 쇼피파이 상태 상수
 */
export const SHOPIFY_STATUS = {
  ACTIVE: 'active',
  ARCHIVED: 'archived',
  DRAFT: 'draft'
};

/**
 * 판매 상태 상수
 */
export const SALE_STATUS = {
  AVAILABLE: 'available',
  SOLD_OUT: 'sold_out',
  PRE_ORDER: 'pre_order',
  DISCONTINUED: 'discontinued'
};

/**
 * 사이트 코드 상수
 */
export const SITE_CODES = {
  MUSINSA: 'musinsa',
  OLIVEYOUNG: 'oliveyoung',
  WEVERSE: 'weverse'
};
